import React, { useState } from 'react';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import { Button, Badge, Row, Col, Modal } from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaListUl } from 'react-icons/fa';
import { toast } from 'react-toastify';

import * as api from '~/Services/Service';
import * as constants from '~/constant';
import { salvarEmExcel } from '~/Utils/Helpers';
import { customStyles } from '~/Styles/reactSelectStyles';
import Header from '~/Components/Header/Header';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import FormularioPesquisa from './components/FormularioPesquisa';
import Paginacao from './components/Paginacao';

// Resultado paginado no client: a lista vem inteira do backend, mas só
// renderizamos uma fatia por vez para não sobrecarregar a tela.
const ITENS_POR_PAGINA = 10;

const OPCOES_TIPO_BUSCA = [
  { value: 'cpf', label: 'CPF' },
  { value: 'prospecto', label: 'Prospecto (código)' },
  { value: 'nome', label: 'Nome' },
];

const OPCOES_TIPO_PESQUISA = [
  { value: 'A', label: 'Ambos' },
  { value: 'E', label: 'Entrada' },
  { value: 'S', label: 'Saída' },
];

const OPCOES_FORMULARIO_PESQUISA = OPCOES_TIPO_PESQUISA.filter(
  opcao => opcao.value !== 'A'
);

// Backend manda status por tipo. Contrato: 'respondida' | 'pendente'.
// Tolerante a boolean para não quebrar silenciosamente — confirmar no JSON real.
const isRespondida = status => status === 'respondida' || status === true;

const statusDoTipo = (pessoa, tipo) =>
  tipo === 'E' ? pessoa.status_entrada : pessoa.status_saida;

// id estável para a key da TabelaDinamica (origem+codigo é único por pessoa).
const comId = pessoas =>
  (pessoas || []).map(p => ({ ...p, id: `${p.origem}-${p.codigo}` }));

// Ordenação no parent (controlada): com paginação client-side, deixar a
// TabelaDinamica ordenar só ordenaria a página visível. Ordenamos a lista
// inteira aqui e depois fatiamos. Mesma lógica de comparação da TabelaDinamica.
const ordenarPessoas = (pessoas, ordenacao) => {
  if (!ordenacao.coluna) return pessoas;
  return [...pessoas].sort((a, b) => {
    const valorA = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
    const valorB = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';
    if (typeof valorA === 'number' && typeof valorB === 'number') {
      return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
    }
    const strA = String(valorA).toLowerCase();
    const strB = String(valorB).toLowerCase();
    if (strA < strB) return ordenacao.direcao === 'asc' ? -1 : 1;
    if (strA > strB) return ordenacao.direcao === 'asc' ? 1 : -1;
    return 0;
  });
};

export default function PesquisaEvento() {
  const [tipoBusca, setTipoBusca] = useState('cpf');
  const [termo, setTermo] = useState('');
  const [tipoPesquisa, setTipoPesquisa] = useState('A');
  const [pessoas, setPessoas] = useState([]);
  const [pessoaSelecionada, setPessoaSelecionada] = useState(null);
  const [tipoFormulario, setTipoFormulario] = useState(null);
  const [isBuscando, setIsBuscando] = useState(false);
  const [isExportando, setIsExportando] = useState(false);
  const [buscou, setBuscou] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });

  const limparSelecao = () => {
    setPessoaSelecionada(null);
    setTipoFormulario(null);
  };

  const buscar = async () => {
    const termoLimpo =
      tipoBusca === 'cpf' ? termo.replace(/\D/g, '') : termo.trim();
    if (!termoLimpo) {
      toast.warning('Informe um termo para a busca.');
      return;
    }
    try {
      setIsBuscando(true);
      limparSelecao();
      const payload = { termo: termoLimpo, tipo_busca: tipoBusca };
      const resultado = await api.buscarPessoasPesquisaEvento(payload);
      setPessoas(comId(resultado.pessoas));
      setPagina(1);
      setBuscou(true);
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao buscar.';
      toast.error(mensagem);
    } finally {
      setIsBuscando(false);
    }
  };

  const listarPendentes = async () => {
    if (!tipoPesquisa) {
      toast.warning('Selecione o tipo de pesquisa para listar os pendentes.');
      return;
    }
    try {
      setIsBuscando(true);
      limparSelecao();
      const resultado = await api.listarPendentesPesquisaEvento(tipoPesquisa);
      setPessoas(comId(resultado.pessoas));
      setPagina(1);
      setBuscou(true);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao listar pendentes.';
      toast.error(mensagem);
    } finally {
      setIsBuscando(false);
    }
  };

  const exportarExcel = async () => {
    if (!tipoPesquisa) {
      toast.warning('Selecione o tipo de pesquisa para exportar.');
      return;
    }
    try {
      setIsExportando(true);
      await salvarEmExcel({}, `/evento-pesquisa/exportar?tipo=${tipoPesquisa}`);
    } catch (err) {
      toast.error('Erro ao exportar o Excel.');
    } finally {
      setIsExportando(false);
    }
  };

  const aoSalvarPesquisa = tipo => {
    const atualizarStatus = pessoa => {
      if (tipo === 'E') return { ...pessoa, status_entrada: 'respondida' };
      return { ...pessoa, status_saida: 'respondida' };
    };
    setPessoaSelecionada(prev => (prev ? atualizarStatus(prev) : prev));
    setPessoas(prev =>
      prev.map(p =>
        pessoaSelecionada && p.id === pessoaSelecionada.id
          ? atualizarStatus(p)
          : p
      )
    );
    setTipoFormulario(null);
  };

  const renderBadgeStatus = status => (
    <Badge variant={isRespondida(status) ? 'success' : 'warning'}>
      {isRespondida(status) ? 'Respondida' : 'Pendente'}
    </Badge>
  );

  const colunas = [
    { title: 'Nome', key: 'nome', ordenacao: true },
    { title: 'CPF', key: 'cpf_mascarado' },
    { title: 'Vínculo', key: 'vinculo', ordenacao: true },
    {
      title: 'Quadra/Lote',
      key: 'quadra_lote',
      render: value => (value ? value : '-'),
    },
    {
      title: 'Entrada',
      key: 'status_entrada',
      align: 'center',
      render: value => renderBadgeStatus(value),
    },
    {
      title: 'Saída',
      key: 'status_saida',
      align: 'center',
      render: value => renderBadgeStatus(value),
    },
  ];

  const acoes = [
    {
      label: 'Selecionar',
      onClick: pessoa => {
        setPessoaSelecionada(pessoa);
        setTipoFormulario(null);
      },
    },
  ];

  // Ordena a lista inteira, depois fatia a página atual. A TabelaDinamica
  // recebe a ordenação controlada para não reordenar só a fatia visível.
  const pessoasOrdenadas = ordenarPessoas(pessoas, ordenacao);
  const totalPaginas = Math.ceil(pessoas.length / ITENS_POR_PAGINA);
  const inicioPagina = (pagina - 1) * ITENS_POR_PAGINA;
  const pessoasPagina = pessoasOrdenadas.slice(
    inicioPagina,
    inicioPagina + ITENS_POR_PAGINA
  );

  const mudarPagina = novaPagina => {
    if (novaPagina < 1 || novaPagina > totalPaginas) return;
    setPagina(novaPagina);
  };

  // Ao reordenar, volta para a primeira página (item esperado aparece no topo).
  const aoOrdenar = novaOrdenacao => {
    setOrdenacao(novaOrdenacao);
    setPagina(1);
  };

  const tipoBuscaSelecionado = OPCOES_TIPO_BUSCA.find(
    o => o.value === tipoBusca
  );
  const tipoPesquisaSelecionado = tipoPesquisa
    ? OPCOES_TIPO_PESQUISA.find(o => o.value === tipoPesquisa)
    : null;

  return (
    <>
      <Header title="Pesquisa de Evento — SetJardim">
        <Button
          variant="outline-success"
          onClick={exportarExcel}
          disabled={isExportando}
        >
          <FaFileExcel className="mr-2" />
          {isExportando ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </Header>

      <div className="container-fluid py-3">
        {/* Card de busca */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <Row className="align-items-end">
              <Col xs={12} md={3} className="mb-2">
                <label className="small mb-1">Buscar por</label>
                <Select
                  styles={customStyles}
                  options={OPCOES_TIPO_BUSCA}
                  value={tipoBuscaSelecionado}
                  isSearchable={false}
                  onChange={opcao => {
                    setTipoBusca(opcao.value);
                    setTermo('');
                  }}
                />
              </Col>
              <Col xs={12} md={5} className="mb-2">
                <label className="small mb-1">Termo</label>
                {tipoBusca === 'cpf' ? (
                  <MaskedInput
                    type="tel"
                    guide={false}
                    className="form-control"
                    placeholder="000.000.000-00"
                    mask={constants.MASK_CPF}
                    value={termo}
                    onChange={e => setTermo(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && buscar()}
                  />
                ) : (
                  <input
                    type={tipoBusca === 'prospecto' ? 'number' : 'text'}
                    className="form-control"
                    placeholder={
                      tipoBusca === 'prospecto'
                        ? 'Código do prospecto'
                        : 'Nome do cliente/prospecto'
                    }
                    value={termo}
                    onChange={e => setTermo(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && buscar()}
                  />
                )}
              </Col>
              <Col xs={12} md={4} className="mb-2">
                <Button
                  variant="primary"
                  block
                  onClick={buscar}
                  disabled={isBuscando}
                >
                  <FaSearch className="mr-2" />
                  {isBuscando ? 'Buscando...' : 'Buscar'}
                </Button>
              </Col>
            </Row>

            <hr />

            <Row className="align-items-end">
              <Col xs={12} md={4} className="mb-2">
                <label className="small mb-1">Tipo de pesquisa</label>
                <Select
                  styles={customStyles}
                  options={OPCOES_TIPO_PESQUISA}
                  value={tipoPesquisaSelecionado}
                  placeholder="Ambos / Entrada / Saída"
                  isClearable={false}
                  onChange={opcao => setTipoPesquisa(opcao ? opcao.value : 'A')}
                />
              </Col>
              <Col xs={12} md={4} className="mb-2">
                <Button
                  variant="outline-primary"
                  block
                  onClick={listarPendentes}
                  disabled={isBuscando}
                >
                  <FaListUl className="mr-2" />
                  Listar pendentes
                </Button>
              </Col>
            </Row>
          </div>
        </div>

        {/* Resultado da busca — TabelaDinamica em desktop, cards em tablet/mobile.
            Breakpoint lg (992px): abaixo disso, a tabela com 6 colunas + ação corta. */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <h6 className="mb-3">
              Resultados
              {buscou && (
                <span className="text-muted ml-2">
                  ({pessoas.length}{' '}
                  {pessoas.length === 1 ? 'registro' : 'registros'})
                </span>
              )}
            </h6>
            {buscou && pessoas.length === 0 ? (
              <p className="text-muted mb-0">
                Nenhum registro encontrado para os critérios informados.
              </p>
            ) : (
              <>
                <div className="d-none d-lg-block">
                  <TabelaDinamica
                    colunas={colunas}
                    dados={pessoasPagina}
                    action
                    acoes={acoes}
                    ordenacao={ordenacao}
                    onOrdenacaoChange={aoOrdenar}
                    cliqueLinha={pessoa => {
                      setPessoaSelecionada(pessoa);
                      setTipoFormulario(null);
                    }}
                    linhaSelecionada={pessoa =>
                      pessoaSelecionada && pessoa.id === pessoaSelecionada.id
                    }
                  />
                </div>

                <div className="d-lg-none">
                  {pessoasPagina.map(pessoa => (
                    <div
                      key={pessoa.id}
                      className="card border mb-2"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setPessoaSelecionada(pessoa);
                        setTipoFormulario(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setPessoaSelecionada(pessoa);
                          setTipoFormulario(null);
                        }
                      }}
                    >
                      <div className="card-body p-3">
                        <h6 className="mb-2">{pessoa.nome}</h6>
                        <p className="mb-1 small">
                          <span className="text-muted">CPF: </span>
                          {pessoa.cpf_mascarado}
                        </p>
                        <p className="mb-1 small">
                          <span className="text-muted">Vínculo: </span>
                          {pessoa.vinculo}
                        </p>
                        {pessoa.quadra_lote ? (
                          <p className="mb-2 small">
                            <span className="text-muted">Quadra/Lote: </span>
                            {pessoa.quadra_lote}
                          </p>
                        ) : null}

                        <div className="d-flex flex-wrap align-items-center mb-2">
                          <span className="mr-2 small text-muted">
                            Entrada:
                          </span>
                          <span className="mr-3">
                            {renderBadgeStatus(pessoa.status_entrada)}
                          </span>
                          <span className="mr-2 small text-muted">Saída:</span>
                          <span>{renderBadgeStatus(pessoa.status_saida)}</span>
                        </div>

                        <Button
                          variant="primary"
                          size="sm"
                          block
                          onClick={e => {
                            e.stopPropagation();
                            setPessoaSelecionada(pessoa);
                            setTipoFormulario(null);
                          }}
                        >
                          Selecionar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <Paginacao
                  paginaAtual={pagina}
                  totalPaginas={totalPaginas}
                  totalRegistros={pessoas.length}
                  itensPorPagina={ITENS_POR_PAGINA}
                  mudarPagina={mudarPagina}
                />
              </>
            )}
          </div>
        </div>

        {/* Modal único: abre ao Selecionar (chooser) e troca para o formulário ao clicar Responder.
            Cancelar dentro do form volta para o chooser; X do header fecha tudo. */}
        <Modal
          show={!!pessoaSelecionada}
          onHide={limparSelecao}
          backdrop="static"
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title as="h6">
              {tipoFormulario
                ? `Pesquisa de ${tipoFormulario === 'E' ? 'Entrada' : 'Saída'}`
                : 'Selecionar pesquisa'}
              {pessoaSelecionada ? ` — ${pessoaSelecionada.nome}` : ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {pessoaSelecionada && !tipoFormulario && (
              <>
                <div className="mb-3">
                  <p className="mb-1">
                    <small className="text-muted">CPF: </small>
                    {pessoaSelecionada.cpf_mascarado}
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">Vínculo: </small>
                    {pessoaSelecionada.vinculo}
                  </p>
                  {pessoaSelecionada.quadra_lote ? (
                    <p className="mb-1">
                      <small className="text-muted">Quadra/Lote: </small>
                      {pessoaSelecionada.quadra_lote}
                    </p>
                  ) : null}
                </div>

                <hr />

                <p className="mb-2">
                  <strong>Qual pesquisa deseja responder?</strong>
                </p>

                {OPCOES_FORMULARIO_PESQUISA.map(opcao => {
                  const status = statusDoTipo(pessoaSelecionada, opcao.value);
                  const respondida = isRespondida(status);
                  return (
                    <div
                      key={opcao.value}
                      className="d-flex align-items-center justify-content-between mb-2"
                    >
                      <span>
                        Pesquisa de {opcao.label} {renderBadgeStatus(status)}
                      </span>
                      <Button
                        size="sm"
                        variant={respondida ? 'secondary' : 'primary'}
                        disabled={respondida}
                        onClick={() => setTipoFormulario(opcao.value)}
                      >
                        {respondida ? 'Respondida' : 'Responder'}
                      </Button>
                    </div>
                  );
                })}
              </>
            )}

            {pessoaSelecionada && tipoFormulario && (
              <FormularioPesquisa
                key={`${pessoaSelecionada.id}-${tipoFormulario}`}
                tipo={tipoFormulario}
                pessoa={pessoaSelecionada}
                onSalvo={() => aoSalvarPesquisa(tipoFormulario)}
                onCancelar={() => setTipoFormulario(null)}
              />
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}
